<template>
	<div>
		<el-form ref="emailAttachments" :model="attachments">
			<el-form-item label-width="140px" :label="label">
				<small class="mb-2" style="color: red" v-if="attachmentsTotalSize > attachmentsMaxSize">
					Total size of attachments exceeds the {{ attachmentsMaxSize.toFixed(2) }} MB limit, please consider splitting this into
					multiple messages.
				</small>

				<el-select
					v-model="attachments.selected"
					multiple
					filterable
					:placeholder="placeholder"
					popper-class="document-types"
					:loading="loading"
					style="width: 100%"
				>

                    <span v-if="!_.isEmpty(attachmentDocuments)">

                        <div class="title">APPLICATION DOCUMENTS</div>

                        <el-collapse :key="`${group}-application`" v-for="group in attachmentDocuments.keys()" accordion>
                            <el-collapse-item :title="group" :name="group">
                                <el-option
                                    v-for="item in attachmentDocuments.get(group)"
                                    :key="`${item.id}-${Number(_.get(item, 'is_app_document', 0))}_application`"
                                    :label="`${item.name} (${item.filename})`"
                                    :value="`${item.id}-${Number(_.get(item, 'is_app_document', 0))}_application`"
                                ></el-option>
                            </el-collapse-item>
                        </el-collapse>
                    </span>

                    <span v-if="!_.isEmpty(libraryDocuments) && showLibraryAttachments" >
                        <div class="title">LIBRARY DOCUMENTS</div>
                        <el-collapse :key="`${group}-library`" v-for="group in libraryDocuments.keys()" accordion>
                            <el-collapse-item :title="group" :name="group">
                                <el-option
                                    v-for="item in libraryDocuments.get(group)"
                                    :key="`${item.id}-0_library`"
                                    :label="`${item.name} (${item.file_name})`"
                                    :value="`${item.id}-0_library`"
                                ></el-option>
                            </el-collapse-item>
                        </el-collapse>
                    </span>

				</el-select>
				<slot class="float-left"></slot>
				<small :style="{ color: attachmentsTotalSize > attachmentsMaxSize ? 'red' : null }" class="float-right">
					(Total Size: {{ attachmentsTotalSize }} / {{ attachmentsMaxSize.toFixed(2) }} MB)
				</small>
			</el-form-item>

		</el-form>
	</div>
</template>

<script>
	function groupBy(list, keyGetter) {
		const map = new Map()
		list.forEach((item) => {
			const key = keyGetter(item)
			const collection = map.get(key)
			if (!collection) {
				map.set(key, [item])
			} else {
				collection.push(item)
			}
		})

		return map
	}

	import { mapGetters } from "vuex"
    import vue2Dropzone from 'vue2-dropzone'
    import 'vue2-dropzone/dist/vue2Dropzone.min.css'

	export default {
		name: "EmailAttachments",
        components: {
            vueDropzone: vue2Dropzone
        },
		props: {
			applicationId: {
				type: [Number, String],
				required: false,
			},
			recipientType: {
				type: String,
				required: false,
				default: null,
			},
			attachmentsFor: {
				required: false,
				type: [Object],
				default: function () {
					return {}
				},
			},
			preselectAttachments: {
				required: false,
				type: [Array],
				default: function () {
					return []
				},
			},
			filterRejectedAttachments: {
				required: false,
				type: [Boolean],
				default: false,
			},
			appendAttachments: {
				required: false,
				type: [Array],
				default: function () {
					return []
				},
			},
			label: {
				required: false,
				type: String,
				default: "Attachments",
			},
			placeholder: {
				required: false,
				type: String,
				default: "Choose Attachments",
			},
            showLibraryAttachments: {
                required: false,
                type: Boolean,
                default: true
            },
		},
		data() {
			return {
				loading: true,
                documents: [],
				attachmentsTotalSize: 0,
				attachmentsMaxSize: 10,
				attachments: {
					selected: [],
				},
			}
		},
		computed: {
			...mapGetters({
				docDocuments: "documents/all",
                libDocuments: "documentLibrary/getAllDocuments",
			}),
			attachmentDocuments() {
				var thisDocAttachments = new Map()

				let documents = this.selectedAttachmentsFor
					? this.selectedAttachmentsFor
					: !_.isEmpty(this.documents)
					? this.documents
					: this.docDocuments

				if (this.filterRejectedAttachments) {
					documents = documents.filter((document) => document.status !== "Rejected")
				}

                // filter out any document placeholders
                documents = documents.filter(document => document.url)

				if (!_.isEmpty(this.appendAttachments)) {
					this.appendAttachments.forEach((attachment) => {
						if (!_.find(documents, { id: attachment.id })) {
							documents.push(attachment)
						}
					})
				}

				thisDocAttachments = groupBy(documents, (doc) => {
					return (
						doc.document_type && doc.document_category != "markup" ? doc.document_type.document_type || "Other" : "Other"
					).toUpperCase()
				})

				this.loading = false

				return thisDocAttachments
			},
            libraryDocuments() {
                if (!_.isEmpty(this.libDocuments)) {
                    var libAttachments = new Map()
                    libAttachments = groupBy(this.libDocuments, (doc) => {
                        return (
                            doc.document_type && doc.document_category != "markup" ? doc.document_type.document_type || "Other" : "Other"
                        ).toUpperCase()
                    })
                    return libAttachments
                }
                return []
			},
			selectedAttachmentsFor() {
				if (this.recipientType in this.attachmentsFor) {
					if (this.attachmentsFor[this.recipientType]) {
						return this.attachmentsFor[this.recipientType]
					}
				}
				return false
			},
		},
		methods: {
			getDocuments() {
				let headers = {
					headers: { Authorization: "Bearer " + this.$store.getters.token },
				}
				axios.get("/api/application/" + this.applicationId + "/finalise/documents", headers).then(res => {
                    this.documents = res.data.documents
                })
			},
		},
		watch: {
            applicationId: {
                handler(applicationId) {
                    if (typeof applicationId !== 'undefined') {
                        this.getDocuments()
                    }
                },
                immediate: true
            },
			attachments: {
				handler(attachments) {
					let newSize = 0
					attachments.selected.forEach((docOptionId) => {
						const id = parseInt(docOptionId)
						const doc = this.docDocuments.find((e) => e.id === id)
						if (doc) {
							newSize += parseFloat(doc.file_size || 0)
						}
					})
					this.attachmentsTotalSize = parseFloat(newSize).toFixed(2)
					this.$emit("input", attachments.selected)
				},
				deep: true,
				immediate: true,
			},
			preselectAttachments: {
				handler(attachments) {
					attachments.forEach((attachment) => {
						let selected = `${attachment.id}-${Number(_.get(attachment, 'is_app_document', 0))}`
                        let isAttachmentSelected = this.attachments.selected.some(attachment => attachment === selected)
                        if (!isAttachmentSelected) {
						    this.attachments.selected.push(selected)
                        }
					})
				},
				deep: true,
				immediate: true,
			},
			appendAttachments: {
				handler(appendAttachments) {
					if (!_.isEmpty(appendAttachments)) {
						appendAttachments.forEach((attachment) => {
							let selected = `${attachment.id}-${Number(_.get(attachment, 'is_app_document', 0))}`
							let isSelected = this.attachments.selected.find((selectedAttachment) => selectedAttachment == selected)
							if (!isSelected) {
								this.attachments.selected.push(selected)
							}
						})
					}
				},
				immediate: true,
				deep: true,
			},
		},
	}
</script>

<style lang="scss">
	.document-types {
		.el-select-dropdown__list {
			padding: 0rem;

			.el-select-dropdown__item {
				font-size: 12px;
				padding: 0 10px;
				position: relative;
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
				color: #606266;
				height: 30px;
				line-height: 30px;
				box-sizing: border-box;
				cursor: pointer;
			}

			.el-collapse-item__header {
				border-bottom: 1px solid #fbfbfb;
				padding-left: 0.5rem;
				color: #2493ef !important;
			}

            .title {
                height: 40px;
                display: flex;
                align-items: center;
                padding: 0 10px;
                background: #2593ef;
                color: #ffffff;
            }
		}
	}
</style>

